import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  ArrowUpward,
  AttachFile,
  Close,
  Image as ImageIcon,
  OpenInFull,
} from "@mui/icons-material";
import { Mic, OctagonPause, Square } from "lucide-react";
import PopOutTextInput from "./PopOutTextInput";
import PopOutTextDialog from "./PopOutTextDialog";

interface Attachment {
  id: string;
  previewUrl?: string;
  filename?: string;
  type?: string;
  content?: string;
}

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  disabled?: boolean;
  sending?: boolean;
  // Stop/cancel support
  onStop?: () => void;
  // Attachment support
  showAttachments?: boolean;
  attachments?: Attachment[];
  onAttachmentUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAttachment?: (index: number) => void;
  uploadingFiles?: boolean;
  acceptedFileTypes?: string;
  // Dictation support
  showDictation?: boolean;
  isDictating?: boolean;
  isDictationLoading?: boolean;
  onStartDictation?: () => void;
  onStopDictation?: () => void;
  // Pop-out dialog support
  enablePopOut?: boolean;
  popOutDialogTitle?: string;
  popOutDialogDescription?: string;
  popOutRows?: number;
  // Styling
  rows?: number;
  inputId?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  placeholder = "Type a message...",
  disabled = false,
  sending = false,
  onStop,
  showAttachments = false,
  attachments = [],
  onAttachmentUpload,
  onRemoveAttachment,
  uploadingFiles = false,
  acceptedFileTypes = "image/*",
  showDictation = false,
  isDictating = false,
  isDictationLoading = false,
  onStartDictation,
  onStopDictation,
  enablePopOut = true,
  popOutDialogTitle = "Compose Message",
  popOutDialogDescription = "Write your message.",
  popOutRows = 10,
  rows = 2,
  inputId = "chat-input-file-upload",
}) => {
  const theme = useTheme();
  const [popOutDialogOpen, setPopOutDialogOpen] = React.useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <Box>
      {/* Attachment chips */}
      {showAttachments && attachments.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {attachments.map((attachment, index) => {
              const isImage = attachment.previewUrl || attachment.type?.startsWith("image/");
              const tooltipContent = isImage
                ? attachment.previewUrl
                  ? (
                    <img
                      src={attachment.previewUrl}
                      alt={attachment.filename || "Image"}
                      style={{ maxWidth: 200, maxHeight: 200 }}
                    />
                  )
                  : attachment.filename || "Image"
                : attachment.content
                  ? attachment.content.substring(0, 500) + (attachment.content.length > 500 ? "..." : "")
                  : attachment.filename || "Document";
              return (
                <Tooltip key={attachment.id} title={tooltipContent} arrow placement="top">
                  <Chip
                    icon={isImage ? <ImageIcon sx={{ fontSize: 16 }} /> : <AttachFile sx={{ fontSize: 16 }} />}
                    label={attachment.filename || (isImage ? "Image" : "Document")}
                    size="small"
                    variant="outlined"
                    onDelete={onRemoveAttachment ? () => onRemoveAttachment(index) : undefined}
                    sx={{ maxWidth: 200 }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Hidden file input */}
      {showAttachments && onAttachmentUpload && (
        <input
          type="file"
          accept={acceptedFileTypes}
          multiple
          onChange={onAttachmentUpload}
          style={{ display: "none" }}
          id={inputId}
        />
      )}

      {/* Chat-style input container */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          p: 1.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          "&:focus-within": {
            borderColor: "primary.main",
            borderWidth: "2px",
          },
        }}
      >
        {/* Attachment button on the left */}
        {showAttachments && onAttachmentUpload && (
          <label htmlFor={inputId}>
            <IconButton
              component="span"
              disabled={uploadingFiles || disabled}
              sx={{
                color: "text.secondary",
                flexShrink: 0,
              }}
            >
              {uploadingFiles ? (
                <CircularProgress size={24} />
              ) : (
                <AttachFile />
              )}
            </IconButton>
          </label>
        )}

        {/* Text input in the middle */}
        <Box sx={{ flex: 1, position: "relative" }}>
          <PopOutTextInput
            fullWidth
            multiline
            rows={rows}
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            dialogTitle={popOutDialogTitle}
            dialogDescription={popOutDialogDescription}
            popOutRows={popOutRows}
            enablePopOut={false}
            variant="standard"
            disabled={disabled}
            InputProps={{
              disableUnderline: true,
            }}
            sx={{
              "& .MuiInputBase-root": {
                padding: 0,
              },
            }}
          />

          {/* Expand button - show when text exceeds visible rows */}
          {enablePopOut && (value.split("\n").length > rows || value.length > 80 * rows) && (
            <IconButton
              onClick={() => setPopOutDialogOpen(true)}
              size="small"
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                color: theme.palette.text.secondary,
                opacity: 0.6,
                zIndex: 2,
                "&:hover": {
                  opacity: 1,
                },
              }}
            >
              <OpenInFull fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Dictation button */}
        {showDictation && onStartDictation && onStopDictation && (
          <Tooltip title={isDictating ? "Stop dictation" : "Start dictation"}>
            <IconButton
              onClick={isDictating ? onStopDictation : onStartDictation}
              disabled={isDictationLoading || disabled}
              sx={{
                color: isDictating
                  ? theme.palette.error.main
                  : theme.palette.text.secondary,
                flexShrink: 0,
              }}
            >
              {isDictationLoading ? (
                <CircularProgress size={24} />
              ) : isDictating ? (
                <Square size={20} fill="currentColor" />
              ) : (
                <Mic size={20} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* Send/Stop button on the right */}
        {sending && onStop && !value.trim() ? (
          // Show stop button only when sending AND no new text entered
          <Tooltip title="Stop generating">
            <IconButton
              onClick={onStop}
              sx={{
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
                width: 40,
                height: 40,
                flexShrink: 0,
                position: "relative",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {/* Spinning border */}
              <CircularProgress
                size={36}
                thickness={2}
                sx={{
                  position: "absolute",
                  color: "inherit",
                  opacity: 0.6,
                }}
              />
              {/* Stop icon */}
              <OctagonPause size={18} />
            </IconButton>
          </Tooltip>
        ) : (
          // Show send button - enabled when there's text, even while AI is generating
          <IconButton
            onClick={onSend}
            disabled={!value.trim() || disabled}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark" ? "#ffffff" : "#000000",
              color: theme.palette.mode === "dark" ? "#000000" : "#ffffff",
              width: 40,
              height: 40,
              flexShrink: 0,
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#f0f0f0" : "#333333",
              },
              "&:disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            {sending && !value.trim() ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <ArrowUpward />
            )}
          </IconButton>
        )}
      </Box>

      {/* Pop Out Text Dialog */}
      {enablePopOut && (
        <PopOutTextDialog
          open={popOutDialogOpen}
          onClose={() => setPopOutDialogOpen(false)}
          onSave={(newValue) => {
            onChange(newValue);
            setPopOutDialogOpen(false);
          }}
          value={value}
          title={popOutDialogTitle}
          description={popOutDialogDescription}
          placeholder={placeholder}
          rows={popOutRows}
        />
      )}
    </Box>
  );
};

export default ChatInput;
